import { useModal } from "@ebay/nice-modal-react";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldClaimMapping from "components/FormFields/ClaimMapping";
import { EMPTY_CLAIM_MAPPING_VALUE } from "components/FormFields/ClaimMapping";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import useTypedContext from "hooks/useTypedContext";
import { SamlSettings } from "types/generated";
import { getDrawerFormFix } from "utils/css";

import { UPDATE_SSO_SAML } from "./gql";
import { SettingsSingleSignOnEditSAMLFormFields } from "./helpers";

const useSAMLUpdate = createDefaultMutationHook(UPDATE_SSO_SAML);

type SettingsSingleSignOnEditSAMLProps = {
  integration: SamlSettings;
  setIsDirty: (isDirty: boolean) => void;
};

const SettingsSingleSignOnEditSAML = ({
  integration,
  setIsDirty,
}: SettingsSingleSignOnEditSAMLProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);
  const modal = useModal();

  const [samlUpdate, { loading }] = useSAMLUpdate({
    refetchQueries: ["GetSSOSettings"],
  });

  const form = useForm<SettingsSingleSignOnEditSAMLFormFields>({
    mode: "onChange",
    defaultValues: {
      claimMapping: integration.claimMapping.entries.length
        ? integration.claimMapping.entries
        : [EMPTY_CLAIM_MAPPING_VALUE],
    },
  });

  const {
    handleSubmit,
    formState: { isDirty },
  } = form;

  const onSubmit = handleSubmit((data) => {
    const filteredClaimMapping = data.claimMapping.filter(
      (claim) => claim.name.trim() && claim.value.trim()
    );

    samlUpdate(
      {
        input: {
          claimMapping: { entries: filteredClaimMapping },
        },
      },
      (data) => {
        if (data?.samlUpdate) {
          reportSuccess({ message: "SAML settings updated" });
          modal.hide();
        }
      }
    );
  });

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  return (
    <FormProvider {...form}>
      <DrawerBody hasStickyFooter noPadding>
        <form onSubmit={onSubmit} {...getDrawerFormFix()}>
          <DrawerBody gap="x-large">
            <FormFieldClaimMapping />
          </DrawerBody>

          <DrawerFooter fullWidth sticky>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button variant="primary" type="submit" loading={loading}>
                Save
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </DrawerBody>
    </FormProvider>
  );
};

export default SettingsSingleSignOnEditSAML;
